import React, { useState, useEffect, useCallback } from "react";
import {
  Card, CircularProgress, Chip,
  MenuItem, Select, FormControl, InputLabel,
  TextField, IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import axios from "axios";
import { Notify } from "notiflix";
import { ENDPOINT } from "constants";
import { useSelector } from "react-redux";
import usePermission from "hooks/usePermission";
import NoPermissionNotice from "components/NoPermissionNotice";

const STATUS_COLOR = {
  active: "success",
  unsubscribed: "default",
};

const NewsletterSubscribers = () => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;
  const canRead = usePermission("leads-management", "read");
  const canDelete = usePermission("leads-management", "delete");

  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });

  const fetchSubscribers = useCallback(async (page = pagination.page) => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();

      const { data } = await axios.get(`${ENDPOINT}api/v1/newsletter/subscribers`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data?.success || data?.statusCode === 200) {
        setSubscribers(data.data?.subscribers || []);
        setPagination(data.data?.pagination || { total: 0, page: 1, totalPages: 1 });
      } else {
        Notify.failure(data?.message || "Failed to fetch subscribers");
      }
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Error fetching subscribers");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, pagination.page, canRead]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    try {
      const subscriber = subscribers.find((s) => s.id === id);
      await axios.delete(
        `${ENDPOINT}api/v1/newsletter/subscriber/${id}?label=${encodeURIComponent(subscriber?.email || '')}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      Notify.success("Subscriber deleted");
      fetchSubscribers();
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Failed to delete subscriber");
    }
  };

  const columns = [
    { Header: "Email",        accessor: "email",        width: "40%" },
    { Header: "Status",       accessor: "status",       align: "center", width: "15%" },
    { Header: "Subscribed At", accessor: "subscribedAt", width: "30%" },
    { Header: "Actions",      accessor: "actions",      align: "center", width: "15%" },
  ];

  const rows = subscribers.map((sub) => ({
    email: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {sub.email}
      </MDTypography>
    ),
    status: (
      <Chip
        label={sub.status}
        color={STATUS_COLOR[sub.status] || "default"}
        size="small"
        sx={{ fontWeight: 600, fontSize: 11, textTransform: "capitalize" }}
      />
    ),
    subscribedAt: (
      <MDTypography variant="caption" color="text">
        {sub.subscribedAt
          ? new Date(sub.subscribedAt).toLocaleDateString("en-AU", {
              day: "2-digit", month: "short", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            })
          : "—"}
      </MDTypography>
    ),
    actions: canDelete ? (
      <IconButton size="small" color="error" onClick={() => handleDelete(sub.id)}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    ) : null,
  }));

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Newsletter Subscribers. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          {/* Header */}
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="success"
            borderRadius="lg" coloredShadow="success"
            display="flex" justifyContent="space-between" alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Newsletter Subscribers
              {pagination.total > 0 && (
                <MDTypography component="span" variant="caption" color="white" sx={{ ml: 1, opacity: 0.8 }}>
                  ({pagination.total} total)
                </MDTypography>
              )}
            </MDTypography>
            <IconButton size="small" onClick={() => fetchSubscribers()} sx={{ color: "white" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </MDBox>

          {/* Filters */}
          <MDBox px={3} pt={3} pb={1} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchSubscribers(1)}
              sx={{ minWidth: 260 }}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => fetchSubscribers(1)}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </MDBox>

          {/* Table */}
          <MDBox px={2} pb={2}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </MDBox>
            ) : rows.length === 0 ? (
              <MDBox py={6} textAlign="center">
                <MDTypography variant="body2" color="secondary">
                  No subscribers found.
                </MDTypography>
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false}
                noEndBorder
              />
            )}
          </MDBox>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <MDBox px={3} pb={3} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
              <MDButton
                size="small" variant="outlined" color="success"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Prev
              </MDButton>
              <MDTypography variant="caption" color="text">
                Page {pagination.page} of {pagination.totalPages}
              </MDTypography>
              <MDButton
                size="small" variant="outlined" color="success"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next
              </MDButton>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewsletterSubscribers;
